import { ApiError, errorDetail } from "@/api/errors";
import { type FieldMachineOption, listFieldMachines } from "@/api/fieldLeads";
import { useAuth } from "@/app/auth";
import { useOnline } from "@/app/online";
import { useReloginDeferred } from "@/app/relogin";
import { useDebouncedValue } from "@/app/useDebouncedValue";
import { ListError, ListLoading } from "@/screens/common/ListStates";
import { useInfiniteQuery } from "@tanstack/react-query";
import { LockKeyhole, Search, TriangleAlert, WifiOff } from "lucide-react";
import { useId, useState } from "react";
import { machineDescription } from "./fieldLeadView";

const DEBOUNCE_MS = 350;
const PAGE_SIZE = 25;

const NO_SIGNAL_TEXT = "Sin señal: no se puede buscar la máquina, esperá a que vuelva.";
const NO_MATCH_TEXT = "Ninguna máquina coincide con lo que buscaste.";
const NO_STOCK_TEXT = "El concesionario no tiene stock disponible cargado.";
export const PICKER_NO_SESSION_TEXT = "Sin sesión: para buscar la máquina, retomá la sesión.";
export const PICKER_NOT_ENABLED_TEXT =
  "Tu usuario todavía no tiene habilitada la carga de datos del campo. Avisale a la oficina para que te la habiliten.";

function failedWith(error: unknown, status: number): boolean {
  return error instanceof ApiError && error.status === status;
}

export function FieldMachinePicker({
  onPick,
  onCancel,
}: {
  onPick: (option: FieldMachineOption) => void;
  onCancel: () => void;
}) {
  const online = useOnline();
  const deferred = useReloginDeferred();
  const { resumeSession } = useAuth();
  const [resuming, setResuming] = useState(false);
  const fieldId = useId();
  const [text, setText] = useState("");
  const term = useDebouncedValue(text.trim(), DEBOUNCE_MS);

  const query = useInfiniteQuery({
    queryKey: ["field-machines", term],
    enabled: online && !deferred,
    initialPageParam: 0,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      listFieldMachines({
        q: term === "" ? undefined : term,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((sum, page) => sum + page.items.length, 0);
      return last.items.length > 0 && loaded < last.total ? loaded : undefined;
    },
  });

  const pages = query.data?.pages ?? [];
  const options = pages.flatMap((page) => page.items);
  const total = pages[0]?.total ?? 0;
  const loaded = query.isSuccess;
  const sessionLost = deferred || (query.isError && failedWith(query.error, 401));
  const forbidden = !sessionLost && query.isError && failedWith(query.error, 403);
  const searchable = online && !sessionLost && !forbidden;

  function resume() {
    setResuming(true);
    void resumeSession().finally(() => setResuming(false));
  }

  return (
    <div className="sheet__form">
      {online ? null : (
        <p className="notice notice--offline">
          <WifiOff size={16} aria-hidden="true" />
          <span>{NO_SIGNAL_TEXT}</span>
        </p>
      )}

      {online && sessionLost ? (
        <div className="notice notice--offline session-notice">
          <LockKeyhole size={16} aria-hidden="true" />
          <div className="session-notice__body">
            <p className="t-small">{PICKER_NO_SESSION_TEXT}</p>
            <button
              type="button"
              className="btn btn--primary session-notice__action"
              disabled={resuming}
              onClick={resume}
            >
              {resuming ? "Retomando…" : "Retomar la sesión"}
            </button>
          </div>
        </div>
      ) : null}

      {online && forbidden ? (
        <p className="notice notice--error">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>{PICKER_NOT_ENABLED_TEXT}</span>
        </p>
      ) : null}

      <label className="sheet__field" htmlFor={fieldId}>
        <span className="sheet__legend">Buscar la máquina</span>
        <span className="search">
          <Search size={18} aria-hidden="true" />
          <input
            id={fieldId}
            className="sheet__input search__input"
            value={text}
            autoComplete="off"
            placeholder="Marca, modelo o tipo"
            disabled={!searchable}
            onChange={(event) => setText(event.target.value)}
          />
        </span>
      </label>

      {searchable && query.isPending ? <ListLoading label="Buscando en el stock…" /> : null}

      {searchable && query.isError ? (
        <ListError
          message={errorDetail(query.error, "No se pudieron cargar las máquinas")}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {loaded && options.length === 0 ? (
        <p className="t-small">{term === "" && total === 0 ? NO_STOCK_TEXT : NO_MATCH_TEXT}</p>
      ) : null}

      {options.length > 0 ? (
        <ul className="cards">
          {options.map((option) => (
            <li key={option.id}>
              <button type="button" className="card card--tap" onClick={() => onPick(option)}>
                <span className="t-body-strong">{option.label}</span>
                <span className="t-small">{machineDescription(option)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {query.hasNextPage ? (
        <button
          type="button"
          className="btn btn--block"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? "Cargando…" : "Cargar más"}
        </button>
      ) : null}

      <button type="button" className="btn btn--block" onClick={onCancel}>
        Volver al formulario
      </button>
    </div>
  );
}
